// src/components/transportation/Routes.js

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions as MuiDialogActions,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Snackbar,
  Autocomplete,
  Stack,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { alpha, styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsBus as DirectionsBusIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import EmptyState from '../common/EmptyState';

// Styled Components for Enhanced Design
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 6,
  boxShadow: 'none',
  border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
}));

const ListCard = styled(AdminPanelCard)(({ theme }) => ({
  borderRadius: 6,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.dark,
      borderWidth: 2,
    },
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(1),
  },
}));

const StyledSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.5rem',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  color: theme.palette.secondary.main,
  borderBottom: `2px solid ${theme.palette.secondary.light}`,
  paddingBottom: theme.spacing(1),
}));

// Alert component for Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const NoRoutesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[0],
  textAlign: 'center',
}));

const RoutesPage = () => {
  // State Variables
  const [routes, setRoutes] = useState([]);
  const [busStops, setBusStops] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editRouteId, setEditRouteId] = useState(null);
  const [newRoute, setNewRoute] = useState({
    routeName: '',
    startLocation: '',
    endLocation: '',
  });
  const [selectedBusStops, setSelectedBusStops] = useState([]);
  const [error, setError] = useState(null);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingBusStops, setLoadingBusStops] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Validation Errors
  const [validationErrors, setValidationErrors] = useState({
    routeName: '',
    startLocation: '',
    endLocation: '',
    busStops: '',
  });

  const schoolId = localStorage.getItem('schoolId'); // Get school ID from local storage

  // Fetch routes when the component mounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRoutes();
    fetchBusStops(); // Fetch bus stops on component mount to populate Autocomplete options
  }, []);

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Function to fetch routes from the API
  const fetchRoutes = async () => {
    setLoadingRoutes(true);
    try {
      const response = await axiosInstance.get('/api/routes', {
        headers: { schoolId },
      });
      setRoutes(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch routes:', err);
      setError('Failed to fetch routes.');
      showSnackbar('Error fetching routes.', 'error');
    } finally {
      setLoadingRoutes(false);
    }
  };

  // Function to fetch bus stops from the API
  const fetchBusStops = async () => {
    setLoadingBusStops(true);
    try {
      const response = await axiosInstance.get('/api/bus-stops');
      setBusStops(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch bus stops:', err);
      setError('Failed to fetch bus stops.');
      showSnackbar('Error fetching bus stops.', 'error');
    } finally {
      setLoadingBusStops(false);
    }
  };

  // Handle form field changes
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setNewRoute((prev) => ({ ...prev, [name]: value }));
    // Clear validation error for the field
    setValidationErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Function to get bus stop by ID
  const getBusStopById = (busStopId) => {
    const busStop = busStops.find((busStop) => busStop.busStopId === busStopId);
    if (!busStop) {
      console.warn(`Bus stop with ID ${busStopId} not found`);
    }
    return busStop;
  };

  // Handle form submission for adding or updating a route
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    // Perform validation
    const errors = {};
    if (!newRoute.routeName.trim()) {
      errors.routeName = 'Route Name is required.';
    }
    if (!newRoute.startLocation.trim()) {
      errors.startLocation = 'Start Location is required.';
    }
    if (!newRoute.endLocation.trim()) {
      errors.endLocation = 'End Location is required.';
    }
    if (selectedBusStops.length === 0) {
      errors.busStops = 'At least one bus stop must be selected.';
    }

    setValidationErrors(errors);

    // If there are validation errors, stop submission
    if (Object.keys(errors).length > 0) {
      setFormLoading(false);
      showSnackbar('Please fill in all required fields.', 'error');
      return;
    }

    try {
      // Prepare the route data
      const routeData = {
        routeName: newRoute.routeName,
        startLocation: newRoute.startLocation,
        endLocation: newRoute.endLocation,
      };

      let savedRoute;
      if (editRouteId) {
        // Update existing route
        savedRoute = await axiosInstance.put(`/api/routes/${editRouteId}`, routeData, {
          headers: { schoolId },
        });
      } else {
        // Add new route
        savedRoute = await axiosInstance.post('/api/routes', routeData, {
          headers: { schoolId },
        });
      }

      const routeId = savedRoute.data.routeId;

      if (editRouteId) {
        // Delete existing RouteBusStops for the route
        await axiosInstance.delete(`/api/route-bus-stops/route/${routeId}`, {
          headers: { schoolId },
        });
      }

      // Map selected bus stops to RouteBusStopDTO format
      const routeBusStops = selectedBusStops.map((busStopId, index) => ({
        routeId, // Use the saved routeId
        busStopId, // Use the selected busStopId
        stopOrder: index + 1, // Assign the order based on selection
      }));

      if (routeBusStops.length > 0) {
        // Send routeBusStops array in a single request
        await axiosInstance.post('/api/route-bus-stops', routeBusStops, {
          headers: { schoolId },
        });
      }

      // Refresh routes list
      await fetchRoutes();
      showSnackbar(editRouteId ? 'Route updated successfully.' : 'Route added successfully.', 'success');
      resetForm();
    } catch (err) {
      console.error('Failed to save route:', err);
      setError('Failed to save route.');
      showSnackbar('Failed to save route.', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle editing a route
  const handleEditRoute = async (route) => {
    setEditRouteId(route.routeId);
    setNewRoute({
      routeName: route.routeName || '',
      startLocation: route.startLocation || '',
      endLocation: route.endLocation || '',
    });

    try {
      // Check if bus stops are already fetched; if not, fetch them
      if (busStops.length === 0) {
        await fetchBusStops();
      }

      // Assuming routeBusStops are part of the route data
      const routeBusStops = route.routeBusStops || [];
      routeBusStops.sort((a, b) => a.stopOrder - b.stopOrder);
      const busStopIds = routeBusStops.map((rbs) => rbs.busStop.busStopId); // Fixed to access busStopId correctly
      setSelectedBusStops(busStopIds);
    } catch (err) {
      console.error('Failed to fetch route bus stops:', err);
      setError('Failed to fetch route bus stops.');
      showSnackbar('Failed to fetch route bus stops.', 'error');
    }

    setOpenForm(true);
  };

  // Handle deleting a route
  const handleDeleteRoute = async (routeId) => {
    if (window.confirm('Are you sure you want to delete this route?')) {
      try {
        await axiosInstance.delete(`/api/routes/${routeId}`, {
          headers: { schoolId },
        });
        setRoutes((prevRoutes) => prevRoutes.filter((route) => route.routeId !== routeId));
        showSnackbar('Route deleted successfully.', 'success');
      } catch (err) {
        console.error('Failed to delete route:', err);
        setError('Failed to delete route.');
        showSnackbar('Failed to delete route.', 'error');
      }
    }
  };

  // Reset form fields and state
  const resetForm = () => {
    setNewRoute({ routeName: '', startLocation: '', endLocation: '' });
    setSelectedBusStops([]);
    setEditRouteId(null);
    setOpenForm(false);
    setValidationErrors({
      routeName: '',
      startLocation: '',
      endLocation: '',
      busStops: '',
    });
  };

  // Handle opening the Add Route Form
  const handleAddNewRoute = async () => {
    setOpenForm(true);
    setEditRouteId(null);
    setNewRoute({ routeName: '', startLocation: '', endLocation: '' });
    setSelectedBusStops([]);
    setValidationErrors({
      routeName: '',
      startLocation: '',
      endLocation: '',
      busStops: '',
    });

    // Fetch bus stops when opening the form
    if (busStops.length === 0) {
      await fetchBusStops();
    }
  };

  return (
      <Box sx={{ width: '100%' }}>
        <AdminPageHero
          title="Routes Management"
          subtitle="Create route plans, connect bus stops, and organize transportation coverage."
          metrics={[
            { label: 'Routes', value: routes.length },
            { label: 'Stops', value: busStops.length },
          ]}
          actions={
            <StyledButton
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddNewRoute}
            >
              Add New Route
            </StyledButton>
          }
        />

        <Box sx={{ mt: 3 }}>
          <ListCard
            title="Route Directory"
            subtitle="Review transport routes, start-end points, and mapped stops."
          >

        {/* Display error message */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Routes List */}
        {loadingRoutes ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : routes.length === 0 ? ( // Condition to check if routes are empty after loading
          <EmptyState
            title="No routes available"
            description="Create your first route to start organizing transport coverage."
            action={(
              <StyledButton
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddNewRoute}
              >
                Add New Route
              </StyledButton>
            )}
          />
        ) : (
          <Grid container spacing={3}>
            {routes.map((route) => (
              <Grid item xs={12} sm={6} md={4} key={route.routeId}>
                <StyledCard elevation={3}>
                  <StyledCardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <DirectionsBusIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight="medium">
                        {route.routeName}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Start Point:</strong> {route.startLocation}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>End Point:</strong> {route.endLocation}
                    </Typography>
                    {route.routeBusStops && route.routeBusStops.length > 0 && (
                      <>
                        <StyledSectionTitle variant="subtitle2">Bus Stops:</StyledSectionTitle>
                        <List dense>
                          {route.routeBusStops
                            .sort((a, b) => a.stopOrder - b.stopOrder)
                            .map((rbs) => (
                              <ListItem key={rbs.routeBusStopId}>
                                <LocationOnIcon sx={{ mr: 1 }} />
                                <ListItemText
                                  primary={rbs.busStop.name}
                                  secondary={rbs.busStop.address}
                                />
                              </ListItem>
                            ))}
                        </List>
                      </>
                    )}
                  </StyledCardContent>
                  <CardActions>
                    <Tooltip title="Edit">
                      <IconButton color="primary" onClick={() => handleEditRoute(route)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="secondary" onClick={() => handleDeleteRoute(route.routeId)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        )}
          </ListCard>
        </Box>

        {/* Add/Edit Route Dialog */}
        <StyledDialog open={openForm} onClose={resetForm} maxWidth="md" fullWidth>
          <DialogTitle>{editRouteId ? 'Edit Route' : 'Add New Route'}</DialogTitle>
          <form onSubmit={handleFormSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                {/* Route Details */}
                <Grid item xs={12}>
                  <Typography variant="h6">Configure Route</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Route Name"
                    name="routeName"
                    value={newRoute.routeName}
                    onChange={handleFieldChange}
                    required
                    fullWidth
                    margin="normal"
                    error={!!validationErrors.routeName}
                    helperText={validationErrors.routeName}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Start Location"
                    name="startLocation"
                    value={newRoute.startLocation}
                    onChange={handleFieldChange}
                    required
                    fullWidth
                    margin="normal"
                    error={!!validationErrors.startLocation}
                    helperText={validationErrors.startLocation}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="End Location"
                    name="endLocation"
                    value={newRoute.endLocation}
                    onChange={handleFieldChange}
                    required
                    fullWidth
                    margin="normal"
                    error={!!validationErrors.endLocation}
                    helperText={validationErrors.endLocation}
                  />
                </Grid>
                {/* Bus Stops Selection */}
                <Grid item xs={12}>
                  {loadingBusStops ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography variant="body1">Loading Bus Stops...</Typography>
                    </Box>
                  ) : (
                    <Autocomplete
                      multiple
                      options={busStops}
                      getOptionLabel={(option) => option.name || 'Unknown Bus Stop'}
                      value={selectedBusStops
                        .map((id) => getBusStopById(id))
                        .filter((busStop) => busStop !== undefined)}
                      onChange={(event, newValue) => {
                        setSelectedBusStops(newValue.map((busStop) => busStop.busStopId));
                        // Clear validation error for bus stops
                        setValidationErrors((prev) => ({ ...prev, busStops: '' }));
                      }}
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          variant="outlined"
                          label="Select Bus Stops"
                          placeholder="Bus Stops"
                          error={!!validationErrors.busStops}
                          helperText={validationErrors.busStops}
                        />
                      )}
                    />
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <MuiDialogActions>
              <StyledButton onClick={resetForm} color="secondary" variant="outlined">
                Cancel
              </StyledButton>
              <StyledButton type="submit" variant="contained" color="primary" disabled={formLoading}>
                {formLoading ? <CircularProgress size={24} /> : editRouteId ? 'Update Route' : 'Add Route'}
              </StyledButton>
            </MuiDialogActions>
          </form>
        </StyledDialog>

        {/* Snackbar for Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default RoutesPage;
